import { useState } from 'react';
import type { ComputeResponse, InstanceOfferingDetail } from '../api/types';
import { t } from '../App';
import { formatBRL, formatTCO2e } from '../lib/format';
import { Field, TextInput } from './FormControls';

interface Props {
  result: ComputeResponse;
  offer: InstanceOfferingDetail;
  defaultEmail?: string;
  defaultName?: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: (data: { customerEmail: string; customerName?: string; tco2eToBuy: number }) => void;
  i18n?: Record<string, string>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Compensação só em inteiros — 0.4 vira 1, 1.03 vira 2. Convenção OMA:
// crédito de carbono é vendido por unidade (1 tCO₂e), não fracionado.
// Espelha math.Ceil(result.TotalTCO2e) do backend.
function ceilToInt(v: number): number {
  return Math.max(1, Math.ceil(v));
}

export function CheckoutConfirm({
  result,
  offer,
  defaultEmail,
  defaultName,
  loading,
  onCancel,
  onConfirm,
  i18n,
}: Props) {
  const defaultBuy = ceilToInt(result.total_tco2e);
  const [tco2eToBuy, setTco2eToBuy] = useState<number>(defaultBuy);
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [name, setName] = useState(defaultName ?? '');
  const [emailError, setEmailError] = useState<string | undefined>();

  const total = tco2eToBuy * offer.price_brl_per_tco2e;
  const insufficientStock = tco2eToBuy > offer.available_tco2e;

  function handleConfirm() {
    if (!EMAIL_RE.test(email.trim())) {
      setEmailError(t(i18n, 'errors.email_required'));
      return;
    }
    if (insufficientStock) {
      setEmailError(undefined);
      return;
    }
    onConfirm({
      customerEmail: email.trim().toLowerCase(),
      customerName: name.trim() || undefined,
      tco2eToBuy,
    });
  }

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-wider text-tabaco">Revisão antes do pagamento</p>
        <h3 className="font-display text-xl text-floresta mt-1">
          Compensar {formatTCO2e(tco2eToBuy)}
        </h3>
      </div>

      {/* Resumo do projeto */}
      <div className="rounded-lg border border-areia bg-cream/40 overflow-hidden">
        {offer.project_cover_image_url && (
          <div
            className="h-24 bg-center bg-cover"
            style={{ backgroundImage: `url(${offer.project_cover_image_url})` }}
            aria-hidden="true"
          />
        )}
        <div className="p-3">
          <p className="text-2xs uppercase tracking-wider text-cobre">
            {offer.project_category_label ?? offer.project_category} · Vintage {offer.vintage_year}
          </p>
          <p className="font-display text-base text-floresta leading-tight mt-0.5">
            {offer.project_name}
          </p>
          {offer.project_short_description && (
            <p className="text-xs text-ink-subtle mt-1.5">{offer.project_short_description}</p>
          )}
        </div>
      </div>

      {/* Tabela de preço */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-tabaco">Sua emissão calculada</span>
          <span className="tabular-nums">{formatTCO2e(result.total_tco2e)}</span>
        </div>
        <Field
          label="Quanto você quer compensar (tCO₂e)"
          hint={
            insufficientStock
              ? `Estoque disponível neste projeto: ${formatTCO2e(offer.available_tco2e)}.`
              : 'Compensação em unidades inteiras (1, 2, 3…). Arredondamos pra cima por padrão.'
          }
          error={insufficientStock ? t(i18n, 'errors.stock_insufficient') : undefined}
        >
          <TextInput
            type="number"
            inputMode="numeric"
            min={1}
            step="1"
            value={tco2eToBuy}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10);
              setTco2eToBuy(Number.isFinite(raw) ? Math.max(1, raw) : 1);
            }}
          />
        </Field>
        <div className="flex items-center justify-between">
          <span className="text-tabaco">Preço por tCO₂e</span>
          <span className="tabular-nums">{formatBRL(offer.price_brl_per_tco2e)}</span>
        </div>
        <div className="border-t border-nevoa pt-2 flex items-center justify-between font-medium">
          <span className="text-ink">Total</span>
          <span className="text-floresta text-lg tabular-nums">{formatBRL(total)}</span>
        </div>
      </div>

      {/* Dados do comprador */}
      <Field label="E-mail" required error={emailError}>
        <TextInput
          type="email"
          maxLength={160}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(undefined);
          }}
          placeholder="voce@exemplo.com"
          autoComplete="email"
        />
      </Field>
      <Field label="Nome (opcional)" hint="Aparecerá no certificado">
        <TextInput
          type="text"
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Como deve aparecer no certificado"
          autoComplete="name"
        />
      </Field>

      <div className="pt-2 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="text-sm text-tabaco hover:text-floresta transition-colors disabled:opacity-50"
        >
          ← Voltar ao resultado
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading || insufficientStock}
          className="px-5 py-2.5 rounded-md bg-cobre text-cream text-sm font-medium hover:bg-cobre-700 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Preparando pagamento…' : 'Confirmar e pagar'}
        </button>
      </div>

      <p className="text-2xs text-ink-subtle text-center leading-relaxed">
        Você será redirecionado(a) para o ambiente seguro do nosso provedor de pagamento. A OMA
        Ativos Ambientais não armazena dados de cartão.
      </p>
    </div>
  );
}
